import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Icon, Skeleton, cssVars } from "@sagot/ui";
import type { ReactNode } from "react";
import { usePermissions } from "../../../session/permissions";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useFormatters } from "../shared/format";
import { useAgentList } from "../shared/use-engage-data";
import { pointsDelta, type AnalyticsFilters } from "./analytics-model";
import { ChartCard } from "./chart-card";
import { useQuality } from "./use-analytics";

const SOURCES_DIGITS = 1;

function Metric({ label, hint, value, children }: { label: string; hint: string; value: string; children?: ReactNode }) {
  return (
    <div className="rounded-md bg-surface-2 p-3" title={hint}>
      <div className="text-[11px] font-semibold text-fg-subtle">{label}</div>
      <div className="mt-0.5 text-xl font-extrabold tabular-nums">{value}</div>
      {children}
    </div>
  );
}

function RateBar({ ratio }: { ratio: number | null }) {
  return (
    <div aria-hidden="true" className="mt-2 h-1.5 rounded-pill bg-sunken">
      <div className="h-1.5 w-[var(--geo-width)] rounded-pill bg-mint-500" style={cssVars({ "--geo-width": `${((ratio ?? 0) * 100).toFixed(1)}%` })} />
    </div>
  );
}

function RunEvaluationLink({ agentId }: { agentId: string | null }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const agents = useAgentList();
  const target = agentId ?? agents.data?.[0]?.id ?? null;
  const permissions = usePermissions(target);
  if (!target || !permissions.can("evaluations.view", { agentId: target })) return null;
  return (
    <AppLink href={consoleRoutes.evaluations(summary.slug, target)} className="text-xs font-semibold text-primary-ink hover:underline">
      {t("engage.analytics.quality.runEvaluation")}
    </AppLink>
  );
}

/** Answer-quality panel from the automatic grader (`GET /v1/analytics/quality`). */
export function QualityCard({ filters }: { filters: AnalyticsFilters }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const query = useQuality(filters);
  return (
    <ChartCard
      testId="analytics-quality"
      title={
        <>
          <Icon name="shield-check" className="size-4 text-primary-ink" />
          {t("engage.analytics.quality.title")}
        </>
      }
      aside={<RunEvaluationLink agentId={filters.agentId} />}
      bodyClassName="grid grid-cols-2 gap-3"
      query={query}
      isEmpty={(data) => data.answers === 0}
      empty={{ icon: "shield-check" }}
      skeleton={
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      }
    >
      {(data) => {
        const delta = pointsDelta(data.no_answer_delta_pts, (value) => t("engage.analytics.kpi.pts", { value }), false);
        return (
          <>
            <Metric label={t("engage.analytics.quality.groundedness")} hint={t("engage.analytics.quality.groundednessHint")} value={fmt.percent(data.groundedness)}>
              <RateBar ratio={data.groundedness} />
            </Metric>
            <Metric label={t("engage.analytics.quality.citationRate")} hint={t("engage.analytics.quality.citationRateHint")} value={fmt.percent(data.citation_rate)}>
              <RateBar ratio={data.citation_rate} />
            </Metric>
            <Metric
              label={t("engage.analytics.quality.avgSources")}
              hint={t("engage.analytics.quality.avgSourcesHint")}
              value={data.avg_sources_per_answer === null ? "—" : data.avg_sources_per_answer.toFixed(SOURCES_DIGITS)}
            >
              <div className="mt-1 text-[11px] text-fg-subtle">{t("engage.analytics.quality.target")}</div>
            </Metric>
            <Metric label={t("engage.analytics.quality.noAnswer")} hint={t("engage.analytics.quality.noAnswerHint")} value={fmt.percent(data.no_answer_rate)}>
              {delta ? (
                <div className={delta.positive ? "mt-1 flex items-center gap-1 text-[11px] font-semibold text-success" : "mt-1 flex items-center gap-1 text-[11px] font-semibold text-danger"}>
                  <Icon name={delta.direction === "up" ? "trending-up" : "trending-down"} className="size-3" />
                  {delta.label}
                </div>
              ) : null}
            </Metric>
            <p className="col-span-2 text-xs text-fg-subtle">{t("engage.analytics.quality.footnote", { answers: fmt.number(data.answers), count: data.hallucination_flags })}</p>
          </>
        );
      }}
    </ChartCard>
  );
}
